/**
 * Mints the nonce every value-moving flow signs against.
 *
 * Context rather than a hook because four unrelated subtrees (claims, schedule,
 * lease creation, draw claims) need the SAME `isGeneratingNonce` flag to disable
 * their submit buttons together — two wallets' worth of signature prompts racing
 * each other is the failure this prevents.
 */
import { logger } from "@swarm/shared/logger";
import { useCallback, useState } from "react";

import { getJwtNonce } from "@/lib/api";
import { useToast } from "@/ui/shadcn/use-toast";

import { AuthenticationContext } from "./AuthenticationContext";
import {
  AuthenticationContextControllerProps,
  AuthenticationContextType,
} from "./AuthenticationContext.types";

export const AuthenticationContextController = ({
  children,
}: AuthenticationContextControllerProps) => {
  const [isGeneratingNonce, setIsGeneratingNonce] = useState(false);

  const { toast } = useToast();

  const generateNonce = useCallback(
    async (owner: string): Promise<{ nonce: string; nonceToken: string } | null> => {
      if (!owner) {
        logger.error({}, "Owner address is required to generate nonce");

        return null;
      }

      setIsGeneratingNonce(true);

      try {
        const data = await getJwtNonce({ owner });

        // The endpoint answers 200 with an `error` string for business failures
        // (rate limit, malformed owner), so a 2xx is not on its own a success.
        if (data.error) {
          throw new Error(data.error);
        }

        return {
          nonce: data.nonce,
          nonceToken: data.nonceToken,
        };
      } catch (error) {
        logger.error(
          { err: error instanceof Error ? { message: error.message } : error },
          "failed to generate nonce",
        );

        toast({
          title: "Error",
          variant: "destructive",
          description: <p className="mb-0">Failed to generate security nonce.</p>,
        });

        return null;
      } finally {
        setIsGeneratingNonce(false);
      }
    },
    [toast],
  );

  const props: AuthenticationContextType = {
    generateNonce,
    isGeneratingNonce,
  };

  return <AuthenticationContext.Provider value={props}>{children}</AuthenticationContext.Provider>;
};
